import React from "react";
import { useTranslation } from "react-i18next";
import { CustomerLabel } from "../Common/CustomerLabel";
import { HeaderMainTitle } from "../Common/HeaderMainTitle";
import {
  Box,
  Typography,
} from "@mui/material";
import { COUNTRIES, STATES, TIMEZONES } from "@constants";
import { dateWithoutTimezone } from "utils/dateWithoutTimezone";
import { HeaderSubtitle } from "../Common/HeaderSubtitle";
import { useGetClients } from "hooks/api/useGetClients";
import { formatPhoneNumber } from "utils/formatPhoneNumber";
import { translateGender, translateRelationship } from "../utils";
import { getIntlLocale } from "utils/getIntlLocale";

interface DrawerProfileContentProps {
  onBack: () => void;
  user: any;
  currentUserMember?: any;
  isDocumentSigner?: boolean;
  onAvatarSuccess?: (newImageUrl: string) => void;
}

export const DrawerProfileContent: React.FC<DrawerProfileContentProps> = ({
  onBack,
  user,
  currentUserMember,
  isDocumentSigner = false,
  onAvatarSuccess,
}) => {
  const { t } = useTranslation(["clientHeader"]);
  const { clients } = useGetClients(
    isDocumentSigner && user?.id ? { userIds: user.id, role: "document_signer" } : null
  );

  const userInfo = currentUserMember || user;

  const researchParticipants = React.useMemo(() => {
    if (!isDocumentSigner || !clients) return null;

    return clients.map(
      ({ id, agree_to_research_participation, name, child_last_name }: any) => ({
        id,
        fullname: `${name} ${child_last_name}`,
        agreeToResearchParticipation: agree_to_research_participation,
      })
    );
  }, [isDocumentSigner, clients]);

  const renderContent = () => {
    if (!userInfo) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
          <Typography variant="body2" color="text.secondary">
            {t("clientHeader:profile.unableToLoad")}
          </Typography>
        </Box>
      );
    }

    return (
      <>
        <HeaderSubtitle>{t("clientHeader:profile.demographics")}</HeaderSubtitle>
        <Box sx={{ padding: "0 24px" }}>
          <CustomerLabel
            label={t("clientHeader:profile.relationship")}
            value={translateRelationship(t, userInfo?.roleName || "Parent")}
          />
          <CustomerLabel
            label={t("clientHeader:profile.legalGuardian")}
            value={userInfo?.legalGuardian ? t("clientHeader:profile.yes") : t("clientHeader:profile.no")}
          />
          <CustomerLabel
            label={t("clientHeader:profile.designatedCaregiver")}
            value={userInfo?.documentSigner ? t("clientHeader:profile.yes") : t("clientHeader:profile.no")}
          />
          <CustomerLabel
            label={t("clientHeader:profile.firstName")}
            value={userInfo?.first_name || userInfo?.firstName || "-"}
          />
          <CustomerLabel
            label={t("clientHeader:profile.lastName")}
            value={userInfo?.last_name || userInfo?.lastName || "-"}
          />
          <CustomerLabel
            label={t("clientHeader:profile.dateOfBirth")}
            value={
              userInfo?.dob || userInfo?.dateOfBirth
                ? new Date(
                    dateWithoutTimezone(userInfo.dob || userInfo.dateOfBirth)
                  ).toLocaleDateString(getIntlLocale(), {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })
                : "-"
            }
          />
          <CustomerLabel
            label={t("clientHeader:profile.gender")}
            value={translateGender(t, userInfo?.gender)}
          />
          <CustomerLabel
            label={t("clientHeader:profile.phoneNumber")}
            value={formatPhoneNumber(userInfo?.phone_number || userInfo?.phone)}
          />
          <CustomerLabel label={t("clientHeader:profile.email")} value={userInfo?.email || "-"} />
          <CustomerLabel
            label={t("clientHeader:profile.address")}
            value={userInfo?.line1 || userInfo?.address || "-"}
          />
          <CustomerLabel
            label={t("clientHeader:profile.address2")}
            value={userInfo?.line2 || userInfo?.address2 || "-"}
          />
          <CustomerLabel
            label={t("clientHeader:profile.country")}
            value={COUNTRIES[userInfo?.country as keyof typeof COUNTRIES] || "-"}
          />
          <CustomerLabel
            label={t("clientHeader:profile.state")}
            value={STATES[userInfo?.state as keyof typeof STATES] || "-"}
          />
          <CustomerLabel label={t("clientHeader:profile.city")} value={userInfo?.city || "-"} />
          <CustomerLabel label={t("clientHeader:profile.zipCode")} value={userInfo?.zip || "-"} />
          <CustomerLabel
            label={t("clientHeader:profile.timeZone")}
            value={
              TIMEZONES[userInfo?.timezone_jhi as keyof typeof TIMEZONES] ||
              TIMEZONES[userInfo?.timezone as keyof typeof TIMEZONES] ||
              "-"
            }
          />
        </Box>

        <HeaderSubtitle>{t("clientHeader:profile.researchParticipation")}</HeaderSubtitle>
        <Box sx={{ padding: "0 24px" }}>
          {researchParticipants?.length ? (
            researchParticipants.map((participant: any) => (
              <CustomerLabel
                key={participant.id}
                label={participant.fullname}
                value={
                  participant.agreeToResearchParticipation
                    ? t("clientHeader:profile.yes")
                    : t("clientHeader:profile.no")
                }
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t("clientHeader:profile.noResearchData")}
            </Typography>
          )}
        </Box>
      </>
    );
  };

  return (
    <>
      <HeaderMainTitle
        user={user}
        title={t("clientHeader:profile.title")}
        onBack={onBack}
        onAvatarSuccess={onAvatarSuccess}
      />
      {renderContent()}
    </>
  );
};
